import React, { useState, useEffect } from 'react';
import { FaSearch, FaVolumeUp, FaHeart, FaBookmark, FaStar, FaBook, FaGraduationCap, FaBrain, FaLeaf, FaUtensils, FaUsers, FaList, FaPause, FaHeart as FaHeartSolid, FaBookmark as FaBookmarkSolid } from 'react-icons/fa';
import { allVocabulary } from '../data/vocabularyIndex';
import { playSmartAudio } from '../utils/audioUtils';


interface VocabularyWord {
  id: string;
  persian: string;
  english: string;
  transliteration?: string;
  example?: string;
  category?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  audioUrl?: string;
  partOfSpeech?: string;
  tags?: string[];
}

const VocabularyBuilder: React.FC = () => {
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [filteredWords, setFilteredWords] = useState<VocabularyWord[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'alphabetical' | 'difficulty' | 'category'>('alphabetical');
  const [language, setLanguage] = useState<'both' | 'english' | 'farsi'>('both');
  const [favorites, setFavorites] = useState<string[]>([]);
  const [bookmarks, setBookmarks] = useState<string[]>([]);
  const [currentAudio, setCurrentAudio] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Use comprehensive vocabulary data
  const sampleVocabulary: VocabularyWord[] = allVocabulary;


  const categories = [
    { id: 'all', name: 'All Categories', icon: FaBook, color: 'bg-blue-500' },
    { id: 'Greetings', name: 'Greetings', icon: FaUsers, color: 'bg-green-500' },
    { id: 'Family', name: 'Family', icon: FaHeart, color: 'bg-pink-500' },
    { id: 'Food', name: 'Food & Dining', icon: FaUtensils, color: 'bg-orange-500' },
    { id: 'Colors', name: 'Colors', icon: FaLeaf, color: 'bg-purple-500' },
    { id: 'Numbers', name: 'Numbers', icon: FaGraduationCap, color: 'bg-indigo-500' },
    { id: 'Animals', name: 'Animals', icon: FaHeart, color: 'bg-red-500' },
    { id: 'Emotions', name: 'Emotions', icon: FaBrain, color: 'bg-yellow-500' },
    { id: 'Weather', name: 'Weather', icon: FaLeaf, color: 'bg-teal-500' },
    { id: 'Education', name: 'Education', icon: FaBook, color: 'bg-blue-600' },
    { id: 'Politics', name: 'Politics', icon: FaUsers, color: 'bg-gray-600' },
    { id: 'Technology', name: 'Technology', icon: FaBrain, color: 'bg-cyan-500' },
    { id: 'Nature', name: 'Nature', icon: FaLeaf, color: 'bg-emerald-500' },
    { id: 'Society', name: 'Society', icon: FaUsers, color: 'bg-rose-500' },
    { id: 'Professions', name: 'Professions', icon: FaGraduationCap, color: 'bg-indigo-600' }
  ];

  useEffect(() => {
    setWords(sampleVocabulary);
    setFilteredWords(sampleVocabulary);
  }, [sampleVocabulary]);

  useEffect(() => {
    let filtered = words;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(word => 
        word.english.toLowerCase().includes(searchTerm.toLowerCase()) ||
        word.persian.includes(searchTerm) ||
        (word.transliteration && word.transliteration.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(word => word.category === selectedCategory);
    }

    // Filter by difficulty
    if (selectedDifficulty !== 'all') {
      filtered = filtered.filter(word => word.difficulty === selectedDifficulty);
    }

    // Sort words
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'alphabetical':
          return a.english.localeCompare(b.english);
        case 'difficulty':
          const difficultyOrder = { beginner: 1, intermediate: 2, advanced: 3 };
          return (difficultyOrder[a.difficulty || 'beginner'] || 1) - (difficultyOrder[b.difficulty || 'beginner'] || 1);
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        default:
          return 0;
      }
    });

    setFilteredWords(filtered);
  }, [words, searchTerm, selectedCategory, selectedDifficulty, sortBy]);

  const toggleFavorite = (wordId: string) => {
    setFavorites(prev => 
      prev.includes(wordId) 
        ? prev.filter(id => id !== wordId)
        : [...prev, wordId]
    );
  };

  const toggleBookmark = (wordId: string) => {
    setBookmarks(prev => 
      prev.includes(wordId) 
        ? prev.filter(id => id !== wordId)
        : [...prev, wordId]
    );
  };

  const playAudio = (word: VocabularyWord) => {
    if (currentAudio === word.id) {
      setIsPlaying(false);
      setCurrentAudio(null);
      return;
    }

    // Stop any currently playing audio
    if (currentAudio) {
      setIsPlaying(false);
      setCurrentAudio(null);
    }

    try {
      setIsPlaying(true);
      setCurrentAudio(word.id);
      
      // If word has audio URL, use it
      if (word.audioUrl) {
        console.log('Playing audio file for word:', word.english, 'URL:', word.audioUrl);
        
        const audio = new Audio(word.audioUrl);
        
        audio.onended = () => {
          setIsPlaying(false);
          setCurrentAudio(null);
        };
        
        audio.onerror = (error) => {
          console.error('Error playing audio file:', error);
          setIsPlaying(false);
          setCurrentAudio(null);
          // Fallback to text-to-speech
          playSmartAudio(undefined, word.persian);
        };
        
        audio.play().catch(error => {
          console.error('Error playing audio file:', error);
          setIsPlaying(false);
          setCurrentAudio(null);
          // Fallback to text-to-speech
          playSmartAudio(undefined, word.persian);
        });
      } else {
        // Use text-to-speech for words without audio files
        console.log('Using text-to-speech for word:', word.persian);
        playSmartAudio(undefined, word.persian).then(() => {
          setIsPlaying(false);
          setCurrentAudio(null);
        }).catch(error => {
          console.error('Error with text-to-speech:', error);
          setIsPlaying(false);
          setCurrentAudio(null);
        });
      }
      
    } catch (error) {
      console.error('Error in playAudio:', error);
      setIsPlaying(false);
      setCurrentAudio(null);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-6 lg:mb-0">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                📚 Vocabulary Builder
              </h1>
              <p className="text-gray-600">
                Master Persian vocabulary with our comprehensive word collection
              </p>
            </div>
            
            {/* Language Toggle */}
            <div className="flex items-center space-x-4">
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setLanguage('both')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    language === 'both' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Both
                </button>
                <button
                  onClick={() => setLanguage('english')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    language === 'english' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => setLanguage('farsi')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    language === 'farsi' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  فارسی
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search words..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Difficulty Filter */}
            <div>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>

          {/* Sort and View Options */}
          <div className="flex flex-wrap items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="alphabetical">Alphabetical</option>
                <option value="difficulty">Difficulty</option>
                <option value="category">Category</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">View:</span>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400'}`}
              >
                <FaBook className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400'}`}
              >
                <FaList className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FaBook className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Words</p>
                <p className="text-2xl font-bold text-gray-900">{filteredWords.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <FaHeart className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Favorites</p>
                <p className="text-2xl font-bold text-gray-900">{favorites.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <FaBookmark className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Bookmarks</p>
                <p className="text-2xl font-bold text-gray-900">{bookmarks.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FaStar className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Categories</p>
                <p className="text-2xl font-bold text-gray-900">{categories.length - 1}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Vocabulary Grid/List */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredWords.map((word) => (
              <div key={word.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="p-6">
                  {/* Word Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      {language !== 'farsi' && (
                        <h3 className="text-lg font-bold text-gray-900 mb-1">{word.english}</h3>
                      )}
                      {language !== 'english' && (
                        <h3 className="text-xl font-bold text-gray-900 mb-1" dir="rtl">{word.persian}</h3>
                      )}
                      {word.transliteration && (
                        <p className="text-sm text-gray-500 italic">{word.transliteration}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleFavorite(word.id)}
                        className={`p-2 rounded-full transition-colors ${
                          favorites.includes(word.id) 
                            ? 'text-red-500 bg-red-50' 
                            : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                        }`}
                      >
                        {favorites.includes(word.id) ? <FaHeartSolid /> : <FaHeart />}
                      </button>
                      <button
                        onClick={() => toggleBookmark(word.id)}
                        className={`p-2 rounded-full transition-colors ${
                          bookmarks.includes(word.id) 
                            ? 'text-yellow-500 bg-yellow-50' 
                            : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50'
                        }`}
                      >
                        {bookmarks.includes(word.id) ? <FaBookmarkSolid /> : <FaBookmark />}
                      </button>
                    </div>
                  </div>

                  {/* Difficulty Badge */}
                  {word.difficulty && (
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mb-3 ${getDifficultyColor(word.difficulty)}`}>
                      {word.difficulty}
                    </span>
                  )}

                  {/* Category */}
                  {word.category && (
                    <div className="flex items-center mb-3">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">{word.category}</span>
                    </div>
                  )}

                  {/* Example */}
                  {word.example && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-700" dir="rtl">{word.example}</p>
                    </div>
                  )}

                  {/* Audio Button */}
                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={() => playAudio(word)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                        currentAudio === word.id && isPlaying
                          ? 'bg-blue-100 text-blue-600'
                          : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600'
                      }`}
                    >
                      {currentAudio === word.id && isPlaying ? <FaPause /> : <FaVolumeUp />}
                      <span className="text-sm font-medium">
                        {currentAudio === word.id && isPlaying ? 'Playing...' : 'Listen'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Word</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transliteration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difficulty</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredWords.map((word) => (
                    <tr key={word.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{word.english}</div>
                          <div className="text-sm text-gray-500" dir="rtl">{word.persian}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {word.transliteration}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {word.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDifficultyColor(word.difficulty || 'beginner')}`}>
                          {word.difficulty}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => playAudio(word)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <FaVolumeUp />
                          </button>
                          <button
                            onClick={() => toggleFavorite(word.id)}
                            className={favorites.includes(word.id) ? 'text-red-600' : 'text-gray-400 hover:text-red-600'}
                          >
                            {favorites.includes(word.id) ? <FaHeartSolid /> : <FaHeart />}
                          </button>
                          <button
                            onClick={() => toggleBookmark(word.id)}
                            className={bookmarks.includes(word.id) ? 'text-yellow-600' : 'text-gray-400 hover:text-yellow-600'}
                          >
                            {bookmarks.includes(word.id) ? <FaBookmarkSolid /> : <FaBookmark />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredWords.length === 0 && (
          <div className="text-center py-12">
            <FaBook className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No words found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search or filter criteria.</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
                setSelectedDifficulty('all');
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VocabularyBuilder; 